import Interop from "./interfaces/Interop";
import { ThemeContextProvider } from "./context";
import App from "./App";
import { useEffect, useState } from "react";
import { ClientCredential } from "./models/ClientCredential";
import { ApplicationColor } from "./models/ApplicationColor";
import { ChatPageNavigation } from "./models/ChatPageNavigation";
import { ChannelProvider } from '../src/components/ChannelContext/ChannelContext';

type BootComponent = {
  apiKey: string;
  targetOrigin: string;
  interop: Interop;
  onRemount: () => void;
};

/**
 * It's responsible for fetching ALL configuration from the native/web platform
 * before rendering the main App component.
 * 
 * MAIN RESPONSIBILITIES:
 * 1. Fetch user credentials from native/web (userId, token, eventId)
 * 2. Fetch theme colors (primary, secondary, background, etc.)
 * 3. Fetch navigation type (which page to show: channel list, specific channel, etc.)
 * 4. Fetch platform type (iOS, Android, Web, PWA, etc.)
 * 5. Fetch font size (for accessibility)
 * 6. Fetch language (for internationalization)
 * 7. Pass all configuration to App component
 */
const BootComponent = (props: BootComponent) => {
  const { apiKey, targetOrigin, interop, onRemount } = props; 
  /** User Credentials - Authentication and identification, Contains: userId, userToken, eventId, userName, etc.*/
  const [credentials, setCredentials] = useState<ClientCredential | null>(null);
  
  /** Application Colors - Theme customization*/
  const [colors, setColors] = useState<ApplicationColor | null>(null);
  
  /** Page Navigation - Initial page to show*/
  const [pageType, setPageType] = useState<ChatPageNavigation | null>(null);
  
  /** Font Size - Accessibility setting, Used by: CSS variables for text scaling*/
  const [fontSize, setFontSize] = useState<Number | null>(null);
  
  /** Platform Type - Device/environment identification*/
  const [platformTypes, setPlatformType] = useState<string | null>(null);
  
  /** Language - Internationalization, Used by: Translation/localization throughout the app*/
  const [language, setLanguage] = useState<string>();
  
  /**
   * This useEffect runs ONCE when BootComponent mounts (empty dependency array)
   */
  useEffect(() => {
    // Identifies the device/environment (iOS, Android, Web, etc.), Used for platform-specific UI adjustments and features
    async function getPlatformType() {
      const platformType = await interop.GetPlatformType();
      return platformType;
    }

    // Fetches authentication data needed to connect to Stream Chat, Contains: userId, token, eventId, etc.
    async function getCredentials() {
      const credentials = await interop.GetCredentials();
      return credentials;
    }

    // Fetches theme colors from native app or web configuration, Used to customize the entire app's appearance
    async function getColors() {
      const colors = await interop.GetColors();
      return colors;
    }

    // Determines which page/view to show initially
    async function getPageType() {
      const pageType = await interop.GetPageType();
      return pageType;
    }

    // Fetches user's preferred text size for accessibility
    async function getFontSize() {
      const fontSize = await interop.GetFontSize();
      return fontSize;
    }
    
    // Fetches user's preferred language for internationalization
    async function getLanguage() {
      let language = await interop.GetLanguage();
      return language;
    }
    
    getPlatformType().then((val) => {
      setPlatformType(val);
    });

    getCredentials().then((val) => {
      setCredentials(val);
    });

    getColors().then(val => {
      setColors(val);
    });

    getPageType().then(val => setPageType(val));
    
    getFontSize().then(val => setFontSize(val));

    getLanguage().then(val => setLanguage(val));
  
  }, []); // Empty array = run once on mount, never again



  return (
    // Only render App component AFTER all configuration data is fetched
    <>
      {credentials && language && colors && pageType && fontSize && (
        <ThemeContextProvider targetOrigin={targetOrigin} themeValue={colors.Theme}>
          <ChannelProvider>
            <App
              apiKey={apiKey}
              credentials={credentials}
              targetOrigin={targetOrigin}
              colors={colors}
              pageType={pageType}
              interop={interop}
              fontSize={fontSize}
              platformTypes ={platformTypes!!}
              language={language}
              onRemount={onRemount}
            />
          </ChannelProvider>
        </ThemeContextProvider>
      )}
    </>
  );
};

export default BootComponent;
